import { useEffect } from "react";
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";

import { useWaypoint } from "../common/waypoint/WaypointProvider.jsx";
import { initialsOf, avatarTone } from "../Trainer/Attendance/AttendanceBits.jsx";
import { getStatusMeta } from "../../../utils/eventStatus";
import { getPriorityMeta } from "../../../utils/eventPriority";

/**
 * One mock interview in full -- the /mock-interviews view popup.
 *
 * Built to view_popup_rules in instruction_ui.json, which is to say exactly
 * like the /list-attendance record popup (AttendanceDetailModal): the same
 * frame, head, sections, tables and footer, and no class that popup does not
 * already use. It replaced a SweetAlert built from an HTML string with its
 * own mi-view-* look, emoji labels and a window.* callback.
 *
 * `row` is the list row and `stat` its turnout from
 * GET /mockinterviews/event-stats -- both already loaded by the page, so
 * nothing is fetched here. Every figure is the API's.
 */

const DASH = "—";

// Status and priority as the kit's pill tones. Colour is a tag, never the
// colour of a figure, and the kit has good and bad only.
const PILL_TONE = {
  Active: "pgm-pill--good",
  High: "pgm-pill--bad",
};

const clean = (value) => {
  const text = String(value ?? "").trim();
  return text && text !== "N/A" ? text : "";
};

// The row carries its trainers and batches as the joined records.
const recordsOf = (value, key) =>
  (Array.isArray(value) ? value : [])
    .map((v) => ({ id: v?.id || v?._id || "", name: v?.[key] || "" }))
    .filter((v) => v.name);

const scoreOf = (s) => {
  if (s.scorePercent === null || s.scorePercent === undefined) return DASH;
  const marks = s.mark !== null && s.totalMark !== null ? `${s.mark}/${s.totalMark} · ` : "";
  return `${marks}${s.scorePercent}%`;
};

const resultOf = (s) => {
  if (!s.evaluated) return { label: "Not evaluated", tone: "" };
  if (String(s.result || "").trim().toLowerCase() === "qualified") {
    return { label: "Qualified", tone: "pgm-pill--good" };
  }
  return { label: clean(s.result) || "Unqualified", tone: "pgm-pill--bad" };
};

// A row that names another record is the bridge: click, Enter or Space.
const openProps = (label, go) => ({
  className: "pgm-row--open",
  tabIndex: 0,
  role: "button",
  "aria-label": label,
  onClick: go,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go();
    }
  },
});

const MockInterviewDetailModal = ({ row, stat, canEdit, canDelete, onClose, onEdit, onDelete }) => {
  const { open } = useWaypoint();

  useEffect(() => {
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // A delete made from here is confirmed in this popup, which then closes.
  const { done } = usePopupDone(Boolean(row));

  if (!row) return null;

  const status = getStatusMeta(row.statusValue);
  const priority = getPriorityMeta(row.priorityValue);
  const trainers = recordsOf(row.trainerId, "trainerName");
  const batches = recordsOf(row.batchId, "name");
  const onlineLink = clean(row.onlineLink);
  const venue = clean(row.offlineAddress);
  const description = clean(row.description);

  const attended = Array.isArray(stat?.attendedList) ? stat.attendedList : [];
  const pending = Array.isArray(stat?.absentList) ? stat.absentList : [];
  const enrolled = stat?.expected ?? attended.length + pending.length;

  // Evaluated first, then those still waiting, so the table reads in the
  // order of what is done.
  const students = [
    ...attended.map((s) => ({ ...s, evaluated: true })),
    ...pending.map((s) => ({ ...s, evaluated: false })),
  ];

  // The popup closes itself before handing off to another record.
  const bridge = (type, id) => {
    if (!id) return;
    onClose();
    open(type, id);
  };

  const pct = (value) => (value === null || value === undefined ? DASH : `${value}%`);

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgm-mock-title"
      onClick={onClose}
    >
      <div className="pg-modal-panel pg-modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        {/* The interview is what the record is about, so it is the title --
            in full, never clipped the way the list column clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-mock-title">
              {row.name || "Mock interview"}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Date</dt>
                <dd>{row.dateDisplay || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Time</dt>
                <dd>{row.timeDisplay || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>
                  <span className={`pgm-pill ${PILL_TONE[status?.label] || ""}`.trim()}>
                    {status?.label || DASH}
                  </span>
                </dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Priority</dt>
                <dd>
                  <span className={`pgm-pill ${PILL_TONE[priority?.label] || ""}`.trim()}>
                    {priority?.label || DASH}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
          <>
          {/* What it covers first: who runs it, for which batches, where. */}
          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Trainers
              <span className="pgm-section-count">{trainers.length}</span>
            </h3>
            {trainers.length === 0 ? (
              <p className="pgm-empty">No trainer is assigned.</p>
            ) : (
              <table className="pgm-topics-table">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    <th>Trainer</th>
                  </tr>
                </thead>
                <tbody>
                  {trainers.map((t, i) => (
                    <tr
                      key={t.id || `${t.name}-${i}`}
                      {...(t.id ? openProps(`Open trainer ${t.name}`, () => bridge("trainer", t.id)) : {})}
                    >
                      <td className="pgm-num">{i + 1}</td>
                      <td>{t.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Batches
              <span className="pgm-section-count">{batches.length}</span>
            </h3>
            {batches.length === 0 ? (
              <p className="pgm-empty">No batch is attached.</p>
            ) : (
              <table className="pgm-topics-table">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    <th>Batch</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b, i) => (
                    <tr
                      key={b.id || `${b.name}-${i}`}
                      {...(b.id ? openProps(`Open batch ${b.name}`, () => bridge("batch", b.id)) : {})}
                    >
                      <td className="pgm-num">{i + 1}</td>
                      <td>{b.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="pgm-section">
            <h3 className="pgm-section-title">Where and what</h3>
            {!onlineLink && !venue && !description ? (
              <p className="pgm-empty">No link, venue or description was given.</p>
            ) : (
              <table className="pgm-topics-table">
                <tbody>
                  {onlineLink && (
                    <tr>
                      <td className="pgm-strong">Online link</td>
                      <td>
                        <a href={onlineLink} target="_blank" rel="noopener noreferrer">
                          {onlineLink}
                        </a>
                      </td>
                    </tr>
                  )}
                  {venue && (
                    <tr>
                      <td className="pgm-strong">Venue</td>
                      <td>{venue}</td>
                    </tr>
                  )}
                  {description && (
                    <tr>
                      <td className="pgm-strong">Description</td>
                      <td>{description}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </section>

          {/* Then the figures -- ink figures in tiles, as the API worked
              them out. */}
          <section className="pgm-section">
            <h3 className="pgm-section-title">Turnout</h3>
            {!stat ? (
              <p className="pgm-empty">Turnout is still loading.</p>
            ) : (
              <div className="pgm-tiles">
                <div className="pgm-tile">
                  <b>{attended.length}</b>
                  <span>Evaluated</span>
                </div>
                <div className="pgm-tile">
                  <b>{pending.length}</b>
                  <span>Not evaluated</span>
                </div>
                <div className="pgm-tile">
                  <b>{enrolled}</b>
                  <span>Enrolled</span>
                </div>
                <div className="pgm-tile">
                  <b>{pct(stat.performanceRate)}</b>
                  <span>Average score</span>
                </div>
                <div className="pgm-tile">
                  <b>
                    {stat.qualified ?? 0} / {stat.unqualified ?? 0}
                  </b>
                  <span>Qualified / not</span>
                </div>
                <div className="pgm-tile">
                  <b>{pct(stat.passRate)}</b>
                  <span>Pass rate</span>
                </div>
              </div>
            )}
          </section>

          {/* Then who they were. */}
          <section className="pgm-section">
            <h3 className="pgm-section-title">
              Students
              <span className="pgm-section-count">{students.length}</span>
            </h3>
            {students.length === 0 ? (
              <p className="pgm-empty">No students are enrolled in this mock interview&rsquo;s batches yet.</p>
            ) : (
              <table className="pgm-table pgm-students">
                <thead>
                  <tr>
                    <th className="pgm-num">#</th>
                    <th>Student</th>
                    <th>Mobile</th>
                    <th>Score</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => {
                    const result = resultOf(s);
                    return (
                      <tr
                        key={s.studentId || `${s.name}-${i}`}
                        {...(s.studentId
                          ? openProps(`Open student ${s.name || ""}`.trim(), () => bridge("student", s.studentId))
                          : {})}
                      >
                        <td className="pgm-num">{i + 1}</td>
                        <td>
                          <div className="pgm-person">
                            <span className={`pgm-avatar pgm-avatar--${avatarTone(s.name)}`} aria-hidden="true">
                              {initialsOf(s.name)}
                            </span>
                            <span className="pgm-person-id">
                              <b className="pgm-person-name">{s.name || DASH}</b>
                              <span className="pgm-person-mail">{s.email || "No email"}</span>
                            </span>
                          </div>
                        </td>
                        <td className="pgm-tel">{s.phone || DASH}</td>
                        <td className="pgm-tel">{scoreOf(s)}</td>
                        <td>
                          <span className={`pgm-pill ${result.tone}`.trim()}>{result.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
          </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end --
            you read the record, then act on it. Close stays last and is the
            only filled button, so the destructive one is never the default
            thing under the cursor. A control the viewer cannot use is not
            drawn. */}
        {!done && (
        <footer className="pg-modal-foot">
          <div className="pgm-actions">
            {canEdit && (
              <button type="button" className="pgm-btn" onClick={onEdit}>
                Edit
              </button>
            )}
            {canDelete && (
              <button type="button" className="pgm-btn pgm-btn--danger" onClick={onDelete}>
                Delete
              </button>
            )}
          </div>
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
            Close
          </button>
        </footer>
        )}
      </div>
    </div>
  );
};

export default MockInterviewDetailModal;
